import classnames from 'classnames';
import { useMemo, useState, Fragment } from 'react';
import { ClickableDiv } from "../../../../helper/components/ClickableDiv.js";
import { MaybeWrapper } from "../../../../helper/components/MaybeWrapper.js";
import { useStableId } from "../../../../helper/hooks/useStableId.js";
import { Notification } from "../../../notification/Notification.js";
import { Spinner } from "../../../spinner/Spinner.js";
import { useFocusControl } from "../../hooks/useFocusControl.js";
import { isAsyncData } from "../../utils/isAsyncData.js";
import { isCustomData } from "../../utils/isCustomData.js";
import { isStaticData } from "../../utils/isStaticData.js";
import { useDefaultFetch } from "../../utils/useDefaultFetch.js";
import { DataTreeBar } from "../DataTreeBar/DataTreeBar.js";
import styles from "./DataTreeNode.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function DataTreeNode({
  data,
  level,
  barProps = {},
  useFetchData = useDefaultFetch,
  loadMoreText = '... weitere Elemente laden'
}) {
  const id = useStableId();
  // use three different open states to differentiate between closed and not opened yet
  const [openStatus, setOpenStatus] = useState(data.defaultOpen ? 'open' : 'initial');
  const {
    data: fetchData,
    status: fetchStatus,
    fetchNextPage,
    hasNextPage
  } = useFetchData({
    id: data.id,
    data: data.data,
    enabled: isAsyncData(data) && openStatus === 'open'
  });
  const {
    focusElement,
    elementIndex,
    doFocus
  } = useFocusControl(fetchData ?? []);
  const hasChildren = useMemo(() => {
    return isStaticData(data) ||
    // there must be at least one fetched children
    isAsyncData(data) && ((fetchData ?? []).length > 0 || fetchStatus !== 'success');
  }, [data, fetchData, fetchStatus]);
  const Wrapper = hasChildren ? ClickableDiv : 'div';

  // Special handling for custom content
  if (isCustomData(data)) {
    return data.customContent;
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.wrapper,
    style: {
      '--tree-level': level
    },
    "data-testid": "data-tree-node",
    children: [/*#__PURE__*/_jsx(MaybeWrapper, {
      condition: hasChildren,
      wrapper: c => /*#__PURE__*/_jsx(ClickableDiv, {
        className: styles.clickWrapper,
        onClick: () => setOpenStatus(o => {
          if (o === 'open') {
            return 'closed';
          }
          return 'open';
        }),
        id: `toggle_${id}`,
        "aria-controls": `children_${id}`,
        "aria-expanded": openStatus === 'open',
        "data-testid": "data-tree-toggle",
        children: c
      }),
      children: /*#__PURE__*/_jsx(DataTreeBar, {
        data: data.data,
        level: level,
        chevron: hasChildren ? openStatus === 'open' ? 'down' : 'right' : undefined,
        ...barProps
      })
    }), hasChildren && /*#__PURE__*/_jsxs("div", {
      className: classnames(styles.children, {
        [styles.childrenGrid]: openStatus === 'open'
      }),
      id: `children_${id}`,
      role: "region",
      "aria-labelledby": `toggle_${id}`,
      "aria-hidden": openStatus !== 'open',
      "data-testid": "data-tree-list",
      children: [openStatus !== 'initial' && isStaticData(data) && data.children.map(data => {
        return /*#__PURE__*/_jsx(DataTreeNode, {
          data: data,
          level: level + 1,
          useFetchData: useFetchData,
          barProps: barProps
        }, data.id);
      }), openStatus !== 'initial' && !!fetchData && fetchData.map((data, index) => {
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [index === elementIndex && focusElement, /*#__PURE__*/_jsx(DataTreeNode, {
            data: data,
            level: level + 1,
            useFetchData: useFetchData,
            barProps: barProps
          })]
        }, data.id);
      }), fetchStatus === 'loading' && /*#__PURE__*/_jsx("div", {
        className: classnames(styles.loadingWrapper),
        children: /*#__PURE__*/_jsx(Spinner, {})
      }), fetchStatus === 'error' && /*#__PURE__*/_jsx(Notification, {
        type: "error",
        className: styles.error,
        children: "Eintr\xE4ge konnten nicht abgerufen werden"
      }), hasNextPage && /*#__PURE__*/_jsx(ClickableDiv, {
        className: classnames(styles.clickWrapper, styles.link),
        onClick: e => {
          e.stopPropagation();
          doFocus();
          fetchNextPage();
        },
        children: /*#__PURE__*/_jsx(DataTreeBar, {
          data: {
            text: loadMoreText
          },
          level: level
        })
      })]
    })]
  });
}